import { useEffect, useRef } from "react";

const SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY;
const SCRIPT_SRC = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";

// Inject the Turnstile script on demand so it only loads on pages that need verification.
function ensureTurnstileScript() {
  if (document.querySelector('script[data-turnstile]')) return;
  const s = document.createElement("script");
  s.src = SCRIPT_SRC;
  s.async = true;
  s.defer = true;
  s.setAttribute("data-turnstile", "1");
  document.head.appendChild(s);
}

// Cloudflare Turnstile widget. Calls onToken(token) on success, onToken("") on expiry/error.
export function Turnstile({ onToken }) {
  const containerRef = useRef(null);
  const widgetIdRef = useRef(null);
  const cbRef = useRef(onToken);
  cbRef.current = onToken;

  useEffect(() => {
    let cancelled = false;
    ensureTurnstileScript();

    const render = () => {
      if (cancelled || !window.turnstile || !containerRef.current || widgetIdRef.current) return;
      widgetIdRef.current = window.turnstile.render(containerRef.current, {
        sitekey: SITE_KEY,
        callback: (t) => cbRef.current(t),
        "expired-callback": () => cbRef.current(""),
        "error-callback": () => cbRef.current(""),
        "timeout-callback": () => cbRef.current(""),
      });
    };

    let interval;
    if (window.turnstile) {
      render();
    } else {
      interval = setInterval(() => {
        if (window.turnstile) {
          clearInterval(interval);
          render();
        }
      }, 200);
    }

    return () => {
      cancelled = true;
      if (interval) clearInterval(interval);
      if (widgetIdRef.current && window.turnstile) {
        try { window.turnstile.remove(widgetIdRef.current); } catch (e) { /* noop */ }
        widgetIdRef.current = null;
      }
    };
  }, []);

  return <div ref={containerRef} data-testid="turnstile-widget" className="flex justify-center my-1" />;
}
